import { useEffect, useState, useRef } from "react";
import { UploadBusy } from "./UploadBusy";
import { useTranslation } from "react-i18next";
import { api } from "../api";
import { toast } from "sonner";
import { Folder, FolderPlus, ChevronRight, Trash2, Pencil, Upload, Download, FolderInput, RotateCcw, Home, Eye } from "lucide-react";
import { PdfViewer } from "./PdfViewer";

// R2-9 — Archivio unico "I miei file" (Paziente/Professionista/Struttura).
// Cartelle e file nella stessa vista. Spostamenti SOLO logici (metadati), nessuna copia R2.
export function FileArchive() {
  const { t } = useTranslation();
  const [parent, setParent] = useState(null);          // { id, name } cartella corrente (null = principale)
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [counts, setCounts] = useState({ active: 0, trash: 0 });
  const [trashed, setTrashed] = useState(false);
  const [q, setQ] = useState("");
  const [allFolders, setAllFolders] = useState([]);
  const [moveTarget, setMoveTarget] = useState(null);  // file da spostare
  const [viewer, setViewer] = useState(null);
  const [opening, setOpening] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const parentId = parent?.id || null;

  const load = () => {
    const params = { trashed };
    if (parentId && !q) params.parent_id = parentId;
    if (q) params.q = q;
    api.get("/drive/archive", { params }).then(({ data }) => {
      setFolders(data.folders || []); setFiles(data.files || []);
      setBreadcrumb(data.breadcrumb || []);
      setCounts({ active: data.active_count || 0, trash: data.trash_count || 0 });
    }).catch(() => {});
  };
  const loadAll = () => api.get("/drive/folders/all").then(({ data }) => setAllFolders(data || [])).catch(() => {});
  useEffect(() => { load(); }, [parentId, trashed, q]); // eslint-disable-line
  useEffect(() => { loadAll(); }, []);
  useEffect(() => () => { if (viewer?.url) URL.revokeObjectURL(viewer.url); }, [viewer]);

  const openFolder = (f) => { setQ(""); setTrashed(false); setParent({ id: f.id, name: f.name }); };
  const goCrumb = (i) => { setQ(""); if (i < 0) setParent(null); else setParent(breadcrumb[i]); };

  const createFolder = async () => {
    const name = window.prompt(t("pd.folder_name")); if (!name) return;
    try { await api.post("/drive/folders", { name, parent_id: parentId }); toast.success(t("pd.created")); load(); loadAll(); } catch { toast.error("Error"); }
  };
  const renameFolder = async (f) => {
    const name = window.prompt(t("pd.rename_prompt"), f.name); if (!name || name === f.name) return;
    try { await api.put(`/drive/folders/${f.id}`, { name }); load(); loadAll(); } catch { toast.error("Error"); }
  };
  const moveFolder = async (f, pid) => {
    if (pid === f.id) return;
    try { await api.put(`/drive/folders/${f.id}`, { name: f.name, parent_id: pid || null }); toast.success(t("pd.moved")); load(); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail === "cannot_move_into_descendant" ? "—" : "Error"); }
  };
  const delFolder = async (f) => {
    if (!window.confirm(t("pd.del_confirm", { name: f.name }))) return;
    try { await api.delete(`/drive/folders/${f.id}`); toast.success(t("pd.deleted_refs")); load(); loadAll(); } catch { toast.error("Error"); }
  };
  const uploadHere = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ""; if (!file || uploading) return;
    if (file.size > 50 * 1024 * 1024) return toast.error("Max 50MB");
    const fd = new FormData(); fd.append("file", file); if (parentId) fd.append("folder_id", parentId);
    setUploading(true);
    try { await api.post("/drive/upload", fd); toast.success(t("pd.uploaded")); load(); }
    catch (err) { toast.error(err.response?.data?.detail || t("pd.upload_err")); }
    finally { setUploading(false); }
  };
  const renameFile = async (f) => {
    const name = window.prompt(t("pd.rename_prompt"), f.filename); if (!name || name === f.filename) return;
    const fd = new FormData(); fd.append("filename", name);
    try { await api.patch(`/drive/${f.id}`, fd); load(); } catch { toast.error("Error"); }
  };
  const trashFile = async (id) => { try { await api.post(`/drive/${id}/trash`); load(); } catch { toast.error("Error"); } };
  const restoreFile = async (id) => { try { await api.post(`/drive/${id}/restore`); load(); } catch { toast.error("Error"); } };
  const doMove = async (pid) => {
    if (!moveTarget) return;
    try { await api.post(`/drive/${moveTarget.id}/move`, { folder_id: pid || null }); toast.success(t("pd.moved_file")); setMoveTarget(null); load(); }
    catch { toast.error("Error"); }
  };
  const emptyTrash = async () => {
    if (!counts.trash) return;
    if (!window.confirm(t("pd.empty_trash_confirm"))) return;
    try { const { data } = await api.post("/drive/trash/empty"); toast.success(`${t("pd.empty_trash_done")} (${data.emptied})`); load(); }
    catch { toast.error("Error"); }
  };
  const view = async (f, forceDownload) => {
    setOpening(f.id);
    try {
      const { data, headers } = await api.get(`/drive/${f.id}/view`, { responseType: "blob" });
      let ct = (headers["content-type"] || data.type || "application/octet-stream").split(";")[0];
      if (ct === "application/octet-stream" && /\.pdf$/i.test(f.filename)) ct = "application/pdf";
      const blob = new Blob([data], { type: ct });
      if (!forceDownload && ct === "application/pdf") setViewer({ blob, filename: f.filename, contentType: ct });
      else if (!forceDownload && ct.startsWith("image/")) setViewer({ url: URL.createObjectURL(blob), filename: f.filename, contentType: ct });
      else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = f.filename;
        document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch { toast.error(t("drive.open_error")); } finally { setOpening(null); }
  };

  // etichette con percorso per il menu di spostamento
  const folderPath = (f) => {
    const byId = {}; allFolders.forEach((x) => { byId[x.id] = x; });
    const parts = []; let cur = f; let g = 0;
    while (cur && g < 50) { parts.unshift(cur.name); cur = cur.parent_id ? byId[cur.parent_id] : null; g++; }
    return parts.join(" / ");
  };
  const closeViewer = () => { if (viewer?.url) URL.revokeObjectURL(viewer.url); setViewer(null); };

  return (
    <div className="space-y-4" data-testid="file-archive">
      {/* Comandi */}
      <div className="flex items-center gap-2 flex-wrap justify-between">
        <div className="flex items-center gap-1 text-sm text-slate-600 flex-wrap min-w-0" data-testid="archive-breadcrumb">
          <button className="hover:text-blue-600 font-medium inline-flex items-center gap-1" onClick={() => goCrumb(-1)} data-testid="archive-breadcrumb-root"><Home size={14} />{t("pd.main_folder")}</button>
          {!trashed && breadcrumb.map((p, i) => (<span key={p.id} className="flex items-center gap-1 min-w-0">
            <ChevronRight size={14} className="text-slate-400" />
            <button className="hover:text-blue-600 truncate max-w-[140px]" onClick={() => goCrumb(i)}>{p.name}</button>
          </span>))}
        </div>
        <div className="flex gap-2">
          <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" onClick={createFolder} data-testid="archive-new-folder"><FolderPlus size={14} className="inline mr-1" />{t("pd.new_folder")}</button>
          <button className="yp-btn yp-btn-primary !py-1 !px-3 text-xs disabled:opacity-60" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="archive-upload">{uploading ? <UploadBusy /> : <><Upload size={14} className="inline mr-1" />{t("drive.upload")}</>}</button>
          <input ref={fileRef} type="file" accept=".pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={uploadHere} data-testid="archive-upload-input" />
        </div>
      </div>

      {/* Ricerca + vista File/Cestino */}
      <div className="flex items-center gap-2 flex-wrap justify-between">
        <input className="yp-input max-w-xs" placeholder={t("pd.search_files")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="archive-search" />
        <div className="flex gap-2 items-center">
          <button className={`yp-btn !py-1 !px-3 text-xs ${!trashed ? "yp-btn-primary" : "yp-btn-ghost"}`} onClick={() => { setTrashed(false); setQ(""); }} data-testid="archive-view-files">{t("pd.files_label")} ({counts.active})</button>
          <button className={`yp-btn !py-1 !px-3 text-xs ${trashed ? "yp-btn-primary" : "yp-btn-ghost"}`} onClick={() => { setTrashed(true); setQ(""); setParent(null); }} data-testid="archive-view-trash">🗑 {t("drive.trash")} ({counts.trash})</button>
          {trashed && <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs text-red-600 disabled:opacity-40" disabled={!counts.trash} onClick={emptyTrash} data-testid="archive-empty-trash"><Trash2 size={14} className="inline mr-1" />{t("pd.empty_trash")}</button>}
        </div>
      </div>

      {folders.length === 0 && files.length === 0 && (
        <div className="text-slate-400 text-sm" data-testid="archive-empty">{q ? t("pd.no_results") : trashed ? t("drive.empty") : t("pd.archive_empty")}</div>
      )}

      {/* Cartelle prima dei file */}
      {!trashed && folders.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="archive-folders">
          {folders.map((f) => (
            <div key={f.id} className="yp-card p-3 flex items-center gap-2" data-testid="archive-folder-item">
              <button className="flex items-center gap-2 min-w-0 flex-1 text-left" onClick={() => openFolder(f)} data-testid="archive-folder-open">
                <Folder size={18} className="text-amber-500 shrink-0" /><span className="font-medium truncate">{f.name}</span>
              </button>
              <div className="flex gap-1 shrink-0 items-center">
                <button className="p-1 text-slate-400 hover:text-blue-600" onClick={() => renameFolder(f)} title={t("pd.rename")} data-testid="archive-folder-rename"><Pencil size={14} /></button>
                <select className="text-[11px] border border-slate-200 rounded px-1 py-0.5 max-w-[86px]" value="" onChange={(e) => moveFolder(f, e.target.value)} title={t("pd.move")} data-testid="archive-folder-move">
                  <option value="">{t("pd.move")}</option>
                  <option value="">{t("pd.move_root")}</option>
                  {allFolders.filter((x) => x.id !== f.id).map((x) => <option key={x.id} value={x.id}>{folderPath(x)}</option>)}
                </select>
                <button className="p-1 text-slate-400 hover:text-red-600" onClick={() => delFolder(f)} title={t("pd.rename")} data-testid="archive-folder-delete"><Trash2 size={14} /></button>
              </div>
            </div>))}
        </div>)}

      {/* File */}
      {files.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="archive-files">
          {files.map((f) => (
            <div key={f.id} className="yp-card p-4" data-testid="archive-file-item">
              <div className="font-medium truncate" title={f.filename}>{f.filename}</div>
              <div className="text-xs text-slate-500">{(f.size / 1024).toFixed(1)} KB · {new Date(f.created_at).toLocaleDateString()}</div>
              <div className="mt-2 flex gap-1.5 flex-wrap">
                <button className="yp-btn yp-btn-ghost !py-1 !px-2 text-xs" onClick={() => view(f, false)} disabled={opening === f.id} data-testid="archive-file-open"><Eye size={13} className="inline mr-1" />{opening === f.id ? "…" : t("drive.open")}</button>
                <button className="yp-btn yp-btn-ghost !py-1 !px-2 text-xs" onClick={() => view(f, true)} data-testid="archive-file-download"><Download size={13} className="inline" /></button>
                {!trashed && <>
                  <button className="yp-btn yp-btn-ghost !py-1 !px-2 text-xs" onClick={() => renameFile(f)} data-testid="archive-file-rename"><Pencil size={13} className="inline" /></button>
                  <button className="yp-btn yp-btn-ghost !py-1 !px-2 text-xs" onClick={() => setMoveTarget(f)} data-testid="archive-file-move"><FolderInput size={13} className="inline mr-1" />{t("pd.move_to")}</button>
                  <button className="yp-btn yp-btn-ghost !py-1 !px-2 text-xs text-red-600" onClick={() => trashFile(f.id)} data-testid="archive-file-trash"><Trash2 size={13} className="inline" /></button>
                </>}
                {trashed && <button className="yp-btn yp-btn-ghost !py-1 !px-2 text-xs" onClick={() => restoreFile(f.id)} data-testid="archive-file-restore"><RotateCcw size={13} className="inline mr-1" />{t("drive.restore")}</button>}
              </div>
            </div>))}
        </div>)}

      {/* Dialog spostamento file */}
      {moveTarget && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => setMoveTarget(null)} data-testid="move-dialog">
          <div className="bg-white rounded-xl w-full max-w-sm p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold">{t("pd.move_to")}</h3>
            <div className="text-xs text-slate-500 truncate">{moveTarget.filename}</div>
            <div className="max-h-64 overflow-auto space-y-1">
              <button className="w-full text-left text-sm p-2 rounded hover:bg-slate-50 inline-flex items-center gap-2" onClick={() => doMove(null)} data-testid="move-to-main"><Home size={14} />{t("pd.main_folder")}</button>
              {allFolders.map((x) => (
                <button key={x.id} className="w-full text-left text-sm p-2 rounded hover:bg-slate-50 inline-flex items-center gap-2" onClick={() => doMove(x.id)} data-testid="move-to-folder"><Folder size={14} className="text-amber-500" />{folderPath(x)}</button>))}
            </div>
            <div className="flex justify-end"><button className="yp-btn yp-btn-ghost" onClick={() => setMoveTarget(null)} data-testid="move-cancel">Annulla</button></div>
          </div>
        </div>)}

      {viewer?.contentType === "application/pdf" && <PdfViewer blob={viewer.blob} filename={viewer.filename} onClose={closeViewer} testid="archive-pdf-viewer" />}
      {viewer && viewer.contentType !== "application/pdf" && (
        <div className="fixed inset-0 z-50 bg-black/70 flex flex-col" onClick={closeViewer} data-testid="archive-viewer-modal">
          <div className="flex justify-between items-center p-3 text-white" onClick={(e) => e.stopPropagation()}>
            <span className="text-sm truncate">{viewer.filename}</span>
            <button className="yp-btn yp-btn-ghost !py-1 !px-3 text-white" onClick={closeViewer} data-testid="archive-viewer-close">✕</button>
          </div>
          <div className="flex-1 flex items-center justify-center p-4 overflow-auto" onClick={(e) => e.stopPropagation()}>
            <img src={viewer.url} alt={viewer.filename} className="max-w-full max-h-full object-contain rounded" data-testid="archive-viewer-image" />
          </div>
        </div>)}
    </div>
  );
}
